<template>
  <section class="history-card" :class="{ card: !embedded }">
    <p class="section-title">{{ $t('OPPORTUNITY_DETAILS.HISTORY') }}</p>
    <ul class="history-list">
      <li v-for="item in histories" :key="item.id" class="history-item">
        <span class="history-dot" />
        <p class="history-line">
          {{ formatDateTime(item.created_at) }} -
          {{ historyActorName(item) }}
        </p>
        <p class="history-change">{{ formatHistory(item) }}</p>
      </li>
    </ul>
  </section>
</template>

<script>
import { temperatureLabelKey } from 'dashboard/helper/opportunityTemperatureHelper';
import {
  activityTypeLabelKey,
  formatActivityDateTime,
} from 'dashboard/helper/opportunityActivityHelper';

const LABEL_ONLY_CHANGE_TYPES = ['created', 'negotiation_form_updated'];

const ACTIVITY_CHANGE_TYPES = [
  'activity_created',
  'activity_completed',
  'activity_canceled',
  'activity_rescheduled',
];

export default {
  name: 'OpportunityHistory',
  props: {
    histories: {
      type: Array,
      default: () => [],
    },
    opportunity: {
      type: Object,
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    formatDateTime(value) {
      if (!value) {
        return '-';
      }

      return new Date(value).toLocaleString('pt-BR');
    },
    historyActorName(item) {
      if (item.user && item.user.name) {
        return item.user.name;
      }
      const key = String(item.source_type || '').toUpperCase();
      const label = this.$t(`OPPORTUNITY_DETAILS.HISTORY_EVENTS.ACTORS.${key}`);
      return label.startsWith('OPPORTUNITY_DETAILS.') ? '-' : label;
    },
    historyEventLabel(changeType) {
      return this.$t(
        `OPPORTUNITY_DETAILS.HISTORY_EVENTS.${String(
          changeType || ''
        ).toUpperCase()}`
      );
    },
    historyValueLabel(changeType, value) {
      if (value === null || value === undefined || value === '') {
        return this.$t('OPPORTUNITY_DETAILS.HISTORY_EVENTS.EMPTY_VALUE');
      }

      if (changeType === 'status_changed') {
        return this.$t(
          `OPPORTUNITY_DETAILS.HISTORY_EVENTS.STATUS_VALUES.${value}`
        );
      }

      if (changeType === 'sla_changed') {
        return this.$t(
          `OPPORTUNITY_DETAILS.HISTORY_EVENTS.SLA_VALUES.${value}`
        );
      }

      if (changeType === 'temperature_changed') {
        return this.$t(temperatureLabelKey(value));
      }

      if (changeType === 'value_changed') {
        return this.formatCurrencyAmount(value);
      }

      return value;
    },
    formatCurrencyAmount(value) {
      const amount = Number(value || 0);
      return `${(this.opportunity && this.opportunity.currency) ||
        'BRL'} ${amount.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    formatActivityHistory(item, label) {
      const activity = item.opportunity_activity;
      const type = activity
        ? this.$t(activityTypeLabelKey(activity.activity_type))
        : '-';

      if (item.change_type === 'activity_created') {
        return `${label}: ${type} - ${formatActivityDateTime(item.new_value)}`;
      }

      if (item.change_type === 'activity_rescheduled') {
        return `${label}: ${type} ${this.$t(
          'OPPORTUNITY_DETAILS.HISTORY_EVENTS.FROM_TO',
          {
            old: formatActivityDateTime(item.old_value),
            new: formatActivityDateTime(item.new_value),
          }
        )}`;
      }

      return `${label}: ${type}`;
    },
    formatHistory(item) {
      const label = this.historyEventLabel(item.change_type);

      if (LABEL_ONLY_CHANGE_TYPES.includes(item.change_type)) {
        return label;
      }

      if (item.change_type === 'note_created') {
        return `${label}: ${item.new_value || '-'}`;
      }

      if (
        item.change_type === 'activity_completed' &&
        item.source_type === 'system'
      ) {
        return this.formatActivityHistory(
          item,
          this.historyEventLabel('activity_auto_completed')
        );
      }

      if (ACTIVITY_CHANGE_TYPES.includes(item.change_type)) {
        return this.formatActivityHistory(item, label);
      }

      const oldValue = this.historyValueLabel(item.change_type, item.old_value);
      const newValue = this.historyValueLabel(item.change_type, item.new_value);

      return `${label} ${this.$t('OPPORTUNITY_DETAILS.HISTORY_EVENTS.FROM_TO', {
        old: oldValue,
        new: newValue,
      })}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  background: var(--s-25);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-medium);
  margin-bottom: var(--space-medium);
  padding: var(--space-medium);
}

.section-title {
  color: var(--s-900);
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-normal);
}

.history-list {
  list-style: none;
  margin: 0;
  max-height: 40rem;
  overflow-y: auto;
  padding: 0;
}

.history-item {
  border-bottom: 1px solid var(--s-75);
  padding: var(--space-small) 0 var(--space-normal) var(--space-large);
  position: relative;
}

.history-item::before {
  background: var(--s-200);
  bottom: 0;
  content: '';
  left: 0.55rem;
  position: absolute;
  top: 1.7rem;
  width: 2px;
}

.history-item:last-child::before {
  display: none;
}

.history-dot {
  background: var(--s-200);
  border-radius: 50%;
  height: 0.9rem;
  left: 0.15rem;
  position: absolute;
  top: 1.05rem;
  width: 0.9rem;
}

.history-line {
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-micro);
}

.history-change {
  color: var(--s-700);
  margin: 0;
}
</style>
